import { motion, AnimatePresence } from 'framer-motion'
import { MessageCircle, Send, Facebook, Mail, X } from 'lucide-react'
import { useState } from 'react'
import env from '@utils/env'
import { contactsAPI } from '@utils/api'
import toast from 'react-hot-toast'

const WhatsAppButton = () => {
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    message: '',
    gdprConsent: false
  })

  const whatsappNumber = env.whatsappNumber
  const whatsappMessage = 'Hi! I am interested in learning more about your DevOps courses.'
  const telegramUrl = env.telegramUrl
  const messengerUrl = env.messengerUrl || 'https://m.me/deoriginlabs'

  const actions = [
    {
      id: 'contact',
      label: 'Contact Us',
      icon: MessageCircle,
      onClick: () => setIsFormOpen(true),
      className: 'bg-violet-600 hover:bg-violet-700'
    },
    {
      id: 'whatsapp',
      label: 'WhatsApp',
      icon: MessageCircle,
      href: `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`,
      className: 'bg-green-500 hover:bg-green-600'
    },
    {
      id: 'telegram',
      label: 'Telegram',
      icon: Send,
      href: telegramUrl,
      className: 'bg-sky-500 hover:bg-sky-600'
    },
    {
      id: 'messenger',
      label: 'Messenger',
      icon: Facebook,
      href: messengerUrl,
      className: 'bg-blue-600 hover:bg-blue-700'
    }
  ]

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.gdprConsent) {
      toast.error('Please accept the privacy policy to proceed')
      return
    }
    setLoading(true)
    try {
      await contactsAPI.submit({ ...formData, source: 'popup' })
      toast.success('Your inquiry has been submitted successfully!')
      setIsFormOpen(false)
      setFormData({
        name: '',
        email: '',
        phone: '',
        country: '',
        message: '',
        gdprConsent: false
      })
    } catch (error) {
      toast.error(error.message || 'Failed to submit inquiry')
    }
    setLoading(false)
  }

  return (
    <>
      <div className="fixed right-0 top-1/2 -translate-y-1/2 z-40 hidden md:flex flex-col items-end">
        {actions.map((action) => {
          const Icon = action.icon
          if (action.href) {
            return (
              <motion.a
                key={action.id}
                href={action.href}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ x: -6 }}
                className={`w-12 h-12 ${action.className} text-white flex items-center justify-center shadow-large transition-colors`}
                aria-label={action.label}
              >
                <Icon className="w-5 h-5" />
              </motion.a>
            )
          }
          return (
            <motion.button
              key={action.id}
              whileHover={{ x: -6 }}
              onClick={action.onClick}
              className={`w-12 h-36 ${action.className} text-white flex items-center justify-center shadow-large transition-colors`}
              aria-label={action.label}
            >
              <span className="-rotate-90 whitespace-nowrap text-sm font-medium tracking-wide">
                {action.label}
              </span>
            </motion.button>
          )
        })}
      </div>

      <div className="fixed bottom-20 right-4 z-40 md:hidden flex flex-col gap-3">
        <button
          onClick={() => setIsFormOpen(true)}
          className="px-4 py-3 rounded-full bg-violet-600 hover:bg-violet-700 text-white shadow-large flex items-center justify-center gap-2 text-sm font-medium"
          aria-label="Contact Us"
        >
          <MessageCircle className="w-5 h-5" />
          Contact Us
        </button>
        <a
          href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="w-14 h-14 rounded-full bg-green-500 hover:bg-green-600 text-white shadow-large flex items-center justify-center"
          aria-label="WhatsApp"
        >
          <MessageCircle className="w-6 h-6" />
        </a>
      </div>

      <AnimatePresence>
        {isFormOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-gray-950/60 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.96 }}
              className="w-full max-w-lg rounded-3xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-large overflow-hidden"
            >
              <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 dark:border-gray-800">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Contact Us</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Talk to our course advisor</p>
                </div>
                <button
                  onClick={() => setIsFormOpen(false)}
                  className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700"
                  aria-label="Close contact form"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <input
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Full Name"
                  className="input-field"
                  required
                />
                <input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Email Address"
                  className="input-field"
                  required
                />
                <input
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Phone Number"
                  className="input-field"
                  required
                />
                <input
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  placeholder="Country"
                  className="input-field"
                  required
                />
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Tell us about your requirement"
                  rows={4}
                  className="textarea-field"
                />
                <label className="flex items-start gap-3 text-sm text-gray-600 dark:text-gray-400">
                  <input
                    type="checkbox"
                    name="gdprConsent"
                    checked={formData.gdprConsent}
                    onChange={handleInputChange}
                    className="checkbox-field mt-1"
                  />
                  <span>I agree to the processing of my personal data.</span>
                </label>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-semibold transition-colors disabled:opacity-60"
                >
                  <Mail className="w-4 h-4" />
                  {loading ? 'Submitting...' : 'Submit Inquiry'}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default WhatsAppButton
